import Controller from '@ember/controller';
import { service } from '@ember/service';
import { computed, action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import validation from '../utils/validation';

const TAB_NAME_LIST = ['Graph', 'Circuit', 'Parts'];

let delay = function (t, v) {
  return new Promise(function (resolve) {
    setTimeout(resolve.bind(null, v), t);
  });
};

////////////////////////////////////////////////////////////////////////////////////
// Index Controller: Controller for the main route of the application,            //
// controlling the flow of information between the UI and the Index Model.        //
////////////////////////////////////////////////////////////////////////////////////

export default class IndexController extends Controller {
  @service feedback;
  @service intl;
  @service logger;
  @service site;
  @service('aditools-analytics') analytics;
  queryParams = ['lang', 'doc'];
  lang = null;
  doc = null;
  systemDialog = null;
  generalDialog = null;
  hasOperationalError = null;
  enabledTabs = null;
  navElement = null;
  width = null;
  height = null;
  stackedTabs = null;
  inputErrors = null;
  circuitErrors = null;

  ////////////////////////////////////////////////////////////////////////////////
  // Initialization                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  init() {
    super.init(...arguments);
    this.enabledTabs = TAB_NAME_LIST.map((item) => {
      return item;
    });
    this.set('systemDialog', { message: '', enabled: false });
    this.set('generalDialog', { title: '', message: '', enabled: false });
    this.set('hasOperationalError', false);
    this.set('stackedTabs', true);
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Computed Properties - used from handlebars                                 //
  ////////////////////////////////////////////////////////////////////////////////

  @computed('width') get screenSize() {
    let result = this.width >= 992 ? 'W' : this.width < 992 && this.width >= 640 ? 'N' : 'T';
    return result;
  }

  @computed('model.selectedTab') get activeTab() {
    return TAB_NAME_LIST[this.model.selectedTab - 1];
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Internal Support Functions                                                 //
  ////////////////////////////////////////////////////////////////////////////////

  _systemDialogFunc(status, message) {
    switch (status) {
      case 'ON':
        this.set('systemDialog.enabled', true);
        this.set('systemDialog.message', this.intl.t(`modals.system.${message}`));
        break;
      case 'OFF':
        this.set('systemDialog.enabled', false);
        this.set('systemDialog.message', '');
        break;
      case 'NONE':
        break;
      default:
        break;
    }
  }

  _validateAndUpdateProjectModel() {
    var s = this.model.selectedPart,
      p = this.model.projectData,
      outputPosSupplies = this.model.isDiffPGIASchematic ? p.vsPosOut : p.vsPos,
      outputNegSupplies = this.model.isDiffPGIASchematic
        ? this.model.isDiffTIAPGIASchematic
          ? p.vsNegOut
          : p.vsNegIn
        : p.vsNeg,
      inputPosSupplies = this.model.isDiffPGIASchematic ? p.vsPosIn : p.vsPos,
      inputNegSupplies = this.model.isDiffPGIASchematic ? p.vsNegIn : p.vsNeg,
      supplyRangeMin = this.model.isDiffPGIASchematic ? s.inputSupplyRangeMin : s.supplyRangeMin,
      supplyRangeMax = this.model.isDiffPGIASchematic ? s.inputSupplyRangeMax : s.supplyRangeMax,
      inVspText = s.inVspText || this.intl.t('inputMain.vsp'),
      inVsmText = s.inVsmText || this.intl.t('inputMain.vsn'),
      outVspText = s.outVspText || this.intl.t('inputMain.vsp'),
      outVsmText = s.outVsmText || this.intl.t('inputMain.vsn'),
      vrefText = s.vocmText || this.intl.t('inputMain.vref'),
      vref1Text = this.intl.t('inputMain.vref1'),
      vref2Text = this.intl.t('inputMain.vref2'),
      vclhiText = this.intl.t('inputMain.vclhi'),
      vclloText = this.intl.t('inputMain.vcllo'),
      vrefPosMargin = this.model.isDiffPGIASchematic ? s.vocmPosMargin : s.inputHeadroomVsp,
      vrefNegMargin = this.model.isDiffPGIASchematic ? s.vocmNegMargin : s.inputHeadroomVsm,
      vouts = p.vDiffLeft == p.vDiffRight ? [this.model.vOutLeft] : [this.model.vOutLeft, this.model.vOutRight],
      vref = this.model.vref,
      supplyMarginInfo = {
        fieldId: ['vsNegInput', 'vsPosInput'],
        validationType: 1,
        args: [inputNegSupplies, inputPosSupplies, supplyRangeMin, supplyRangeMax, inVspText, inVsmText],
      },
      vrefMarginInfo = {
        fieldId: [this.model.isDiffPGIASchematic ? 'vocmInput' : 'vrefInput'],
        validationType: 2,
        args: [
          outputNegSupplies,
          outputPosSupplies,
          vref,
          vrefPosMargin,
          vrefNegMargin,
          vrefText,
          outVspText,
          outVsmText,
        ],
      },
      gainRangeInfo = {
        fieldId: ['gainInput'],
        validationType: 3,
        args: [s.selectedGainInput.id, s.gainRangeMin, s.gainRangeMax, s.gainType, s.gainInputs],
      },
      voutInfo = {
        fieldId: [],
        validationType: 4,
        args: [
          outputNegSupplies,
          outputPosSupplies,
          s.outputAmpOutputNegMargin,
          s.outputAmpOutputPosMargin,
          vouts,
          this.model.isDiffPGIASchematic ? vref : null,
          vrefText,
        ],
      },
      vcmInfo = {
        fieldId: [],
        validationType: 5,
        args: [inputNegSupplies, inputPosSupplies, s.preAmpInputNegMargin, s.preAmpInputPosMargin, this.model.vCms],
      },
      outputSupplyRangeInfo = {
        fieldId: this.model.isDiffTIAPGIASchematic
          ? ['vsPosOutput', 'vsNegOutput']
          : this.model.isDiff3AmpPGIASchematic
            ? ['vsPosOutput', 'vsNegInput']
            : [],
        validationType: 6,
        args: [
          outputNegSupplies,
          outputPosSupplies,
          s.outputSupplyRangeMin,
          s.outputSupplyRangeMax,
          outVspText,
          outVsmText,
        ],
      },
      outputNegSupplyInfo = {
        fieldId: ['vsNegOutput'],
        validationType: 7,
        args: [outputNegSupplies, inputNegSupplies, inputPosSupplies, outVsmText, inVsmText, inVspText],
      },
      outputPosSupplyInfo = {
        fieldId: ['vsPosOutput'],
        validationType: 8,
        args: [outputPosSupplies, inputPosSupplies, outVspText, inVspText],
      },
      vrefSpanInfo = {
        fieldId: ['vref1Input', 'vref2Input'],
        validationType: 9,
        args: [p.vref1, p.vref2, s.vrefSpan],
      },
      vref1MarginInfo = {
        fieldId: ['vref1Input'],
        validationType: 2,
        args: [
          inputNegSupplies,
          inputPosSupplies,
          p.vref1,
          vrefPosMargin,
          vrefNegMargin,
          vref1Text,
          inVspText,
          inVsmText,
        ],
      },
      vref2MarginInfo = {
        fieldId: ['vref2Input'],
        validationType: 2,
        args: [
          inputNegSupplies,
          inputPosSupplies,
          p.vref2,
          vrefPosMargin,
          vrefNegMargin,
          vref2Text,
          inVspText,
          inVsmText,
        ],
      },
      vclhiMarginInfo = {
        fieldId: ['vclhiInput'],
        validationType: 2,
        args: [
          inputNegSupplies,
          inputPosSupplies,
          p.vclhi,
          vrefPosMargin,
          vrefNegMargin,
          vclhiText,
          inVspText,
          inVsmText,
        ],
      },
      vclloMarginInfo = {
        fieldId: ['vclloInput'],
        validationType: 2,
        args: [
          inputNegSupplies,
          inputPosSupplies,
          p.vcllo,
          vrefPosMargin,
          vrefNegMargin,
          vclloText,
          inVspText,
          inVsmText,
        ],
      },
      chargePumpSupplyInfo = {
        fieldId: ['vddcpInput'],
        validationType: 10,
        args: [s.chargePumpSupplyMin, s.chargePumpSupplyMax, p.chargePumpSupply, s.chargePumpSupplyText],
      };

    // Validate Items
    var validationFields = [supplyMarginInfo, gainRangeInfo, voutInfo, vcmInfo],
      threeAmpValidation = [vrefMarginInfo],
      threeAmpClampedValidation = [vrefSpanInfo, vref1MarginInfo, vref2MarginInfo, vclhiMarginInfo, vclloMarginInfo],
      diffPGIAValidation = [outputSupplyRangeInfo],
      diffTIAPGIAValidation = [outputNegSupplyInfo],
      diff3AmpPGIAValidation = [outputPosSupplyInfo],
      chargePumpSupplyValidation = [chargePumpSupplyInfo];

    if (this.model.isThreeAmpClampedSchematic) validationFields.push(...threeAmpClampedValidation);
    else validationFields.push(...threeAmpValidation);

    if (this.model.isDiffPGIASchematic) validationFields.push(...diffPGIAValidation);
    if (this.model.isDiffTIAPGIASchematic) validationFields.push(...diffTIAPGIAValidation);
    if (this.model.isDiff3AmpPGIASchematic) validationFields.push(...diff3AmpPGIAValidation);
    if (this.model.usesChargePumpSupply) validationFields.push(...chargePumpSupplyValidation);

    // Clear the list of invalid fields
    this.set('inputErrors', { fields: [], message: '' });

    // Build Graph Data
    this.model.updateCurrentPolygons();

    // Validate
    var results = validation.validate(validationFields, this.model.currentPolygons, this.intl, s.id);

    // Set Error values based on validation
    this.setProperties({
      'model.validationResults.isAllValid': results[0],
      'model.validationResults.isOperatingRange': results[1],
      'model.validationResults.isGain': results[2],
      'model.validationResults.isVref': results[3],
      'model.validationResults.isVs': results[4],
    });

    if (this.model.validationResults.isAllValid) {
      // Update the plot shading
      this.model.updateInnerPolygon();
      if (this.model.innerPolygonError)
        this.analytics.setWebEvent(this.model.toolConfigData.toolName, 'innerPolyFail', location.hash);
    }

    // Prepare the Polygon data in the Models for Plotting
    this.model.preparePlot();

    let errorMessage = results[6];

    if ((!results[0] || !results[1]) && this.model.recommendedParts.length > 0) {
      // If recommended parts are found when an error is displayed, add it at the bottom
      if (errorMessage.includes('<ul>')) {
        errorMessage =
          errorMessage.slice(0, errorMessage.indexOf('</ul>')) +
          [
            '<li>',
            this.intl.t('schematicValidation.recommendedOne'),
            this.model.numOfRecommendedParts,
            this.intl.t('schematicValidation.recommendedTwo'),
            '</li>',
          ].join('') +
          errorMessage.slice(errorMessage.indexOf('</ul>'));
      } else {
        errorMessage = errorMessage.concat(
          [
            '<ul>',
            '<li>',
            this.intl.t('schematicValidation.recommendedOne'),
            this.model.numOfRecommendedParts,
            this.intl.t('schematicValidation.recommendedTwo'),
            '</li>',
            '</ul>',
          ].join(''),
        );
      }
    }

    this.set('inputErrors', { fields: results[5], message: htmlSafe(errorMessage) });
  }

  _validateAndUpdateCircuitModel() {
    // Clear the list of invalid fields
    this.set('circuitErrors', { fields: [], messages: [] });

    // Build the Circuit Values based on the Project Data
    this.model.circuitModel.buildCircuit(
      this.model.selectedPart,
      this.model.projectData,
      this.model.currentSignalType.id,
      this.model.vref,
    );

    var s = this.model.selectedPart,
      p = this.model.projectData,
      cM = this.model.circuitModel,
      vsNegIn = this.model.isDiffPGIASchematic ? p.vsNegIn : p.vsNeg,
      vsPosIn = this.model.isDiffPGIASchematic ? p.vsPosIn : p.vsPos,
      vsNegOut = this.model.isDiffPGIASchematic
        ? this.model.isDiffTIAPGIASchematic
          ? p.vsNegOut
          : p.vsNegIn
        : p.vsNeg,
      vsPosOut = this.model.isDiffPGIASchematic ? p.vsPosOut : p.vsPos,
      inputNegMargin = s.preAmpInputNegMargin,
      inputPosMargin = s.preAmpInputPosMargin,
      outputAmpOutputNegMargin = s.outputAmpOutputNegMargin,
      outputAmpOutputPosMargin = s.outputAmpOutputPosMargin,
      voutValue = this.model.isDiffPGIASchematic ? cM.negVout.val : cM.vOutCir.val,
      upperMargin = this.model.isDiffTIAPGIASchematic
        ? (Math.abs(p.vDiffSlider) * s.selectedGainInput.id * s.rmirr) / (2 * s.ro)
        : 0;

    if (this.model.isThreeAmpClampedSchematic) {
      // for handling clamped outputs - LT6372
      if (vsPosOut - outputAmpOutputPosMargin > p.vclhi - s.clhimargin) {
        vsPosOut = p.vclhi;
        outputAmpOutputPosMargin = s.clhimargin;
      }
      if (vsNegOut + outputAmpOutputNegMargin < p.vcllo + s.cllomargin) {
        vsNegOut = p.vcllo;
        outputAmpOutputNegMargin = s.cllomargin;
      }
    }

    var negVinInfo = {
        fieldId: ['negVinResult'],
        validationType: 4,
        args: [vsNegIn, vsPosIn, inputNegMargin, inputPosMargin, upperMargin, cM.negVin.val, 'negVin'],
      },
      posVinInfo = {
        fieldId: ['posVinResult'],
        validationType: 4,
        args: [vsNegIn, vsPosIn, inputNegMargin, inputPosMargin, upperMargin, cM.posVin.val, 'posVin'],
      },
      negVinGainInfo = {
        fieldId: ['negVinGainResult'],
        validationType: 6,
        args: [vsNegIn, vsPosIn, s.preAmpOutputNegMargin, s.preAmpOutputPosMargin, cM.negVinGain.val, 'negVinGain'],
      },
      posVinGainInfo = {
        fieldId: ['posVinGainResult'],
        validationType: 6,
        args: [vsNegIn, vsPosIn, s.preAmpOutputNegMargin, s.preAmpOutputPosMargin, cM.posVinGain.val, 'posVinGain'],
      },
      negVoutInfo = {
        fieldId: [this.model.isDiffPGIASchematic ? 'cirNegVOutResult' : 'cirVOutResult'],
        validationType: 5,
        args: [
          vsNegOut,
          vsPosOut,
          outputAmpOutputNegMargin,
          outputAmpOutputPosMargin,
          voutValue,
          this.model.isDiffPGIASchematic ? 'negVout' : 'Vout',
        ],
      },
      posVoutInfo = {
        fieldId: ['cirPosVOutResult'],
        validationType: 5,
        args: [vsNegOut, vsPosOut, s.outputAmpOutputNegMargin, s.outputAmpOutputPosMargin, cM.posVout.val, 'posVout'],
      },
      preAmpOutPosInfo = {
        fieldId: ['cirPreAmpOutPos'],
        validationType: 6,
        args: [vsNegIn, vsPosIn, s.preAmpOutputNegMargin, s.preAmpOutputPosMargin, cM.preAmpOutPos.val, 'preAmpOutPos'],
      },
      preAmpOutNegInfo = {
        fieldId: ['cirPreAmpOutNeg'],
        validationType: 6,
        args: [vsNegIn, vsPosIn, s.preAmpOutputNegMargin, s.preAmpOutputPosMargin, cM.preAmpOutNeg.val, 'preAmpOutNeg'],
      },
      gainAmpPosInfo = {
        fieldId: ['cirGainAmpPos'],
        validationType: 5,
        args: [vsNegIn, vsPosIn, s.vSubtractorNeg, s.vSubtractorPos, cM.gainAmpPos.val, 'gainAmpPos'],
      },
      gainAmpNegInfo = {
        fieldId: ['cirGainAmpNeg'],
        validationType: 5,
        args: [vsNegIn, vsPosIn, s.vSubtractorNeg, s.vSubtractorPos, cM.gainAmpNeg.val, 'gainAmpNeg'],
      },
      validationFields = [negVinInfo, posVinInfo, negVoutInfo];

    switch (Number(this.model.selectedDiagram)) {
      case 1:
      case 2:
      case 5:
      case 6:
        validationFields.push(negVinGainInfo);
        validationFields.push(posVinGainInfo);
        break;
      case 3:
        validationFields.push({
          fieldId: ['negVinGain2AmpResult'],
          validationType: 6,
          args: [
            p.vsNeg,
            p.vsPos,
            s.preAmpOutputNegMargin,
            s.preAmpOutputPosMargin,
            cM.negVinGain2Amp.val,
            'negVinGain',
          ],
        });
        break;
      case 4:
        validationFields.push({
          fieldId: ['vdiffCurrentResult'],
          validationType: 7,
          args: [p.vsNeg, p.vsPos, s.vdir, s.gda, cM.vdiffCurrent.val, 'Vdiff'],
        });
        validationFields.push({
          fieldId: ['vfbResult'],
          validationType: 4,
          args: [p.vsNeg, p.vsPos, s.preAmpInputNegMargin, s.preAmpInputPosMargin, 0, cM.vbf.val, 'VFB'],
        });
        break;
      case 7:
        validationFields.push(posVoutInfo);
        break;
      case 8:
        validationFields.push(posVoutInfo);
        validationFields.push(preAmpOutPosInfo);
        validationFields.push(preAmpOutNegInfo);
        validationFields.push(gainAmpPosInfo);
        validationFields.push(gainAmpNegInfo);
        break;
      default:
        break;
    }
    if (this.model.isThreeAmpOrClampedSchematic) {
      validationFields.push({
        fieldId: ['subtractInResult'],
        validationType: 8,
        args: [p.vsNeg, p.vsPos, s.vSubtractorNeg, s.vSubtractorPos, cM.subtractIn.val, 'SubtractIn'],
      });
    }

    let validationResult = validation.validateInternalCircuitry(validationFields, this.intl);
    this.set('circuitErrors', validationResult);
    this.set('model.validationResults.isIntrnCircuit', validationResult.isValid);

    return validationResult.isValid;
  }

  _setUrl() {
    var hash = '#' + this.model.getURLHash();
    if (hash != location.hash) {
      location.replace(hash);
    }
    this.set('model.urlString', location.href);
  }

  _getDimensionsOnResize() {
    let _width = window.innerWidth;
    let _height = window.innerHeight;
    this.set('width', _width < 0 ? 0 : _width);
    this.set('height', _height < 0 ? 0 : _height);
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Controller Actions                                                         //
  ////////////////////////////////////////////////////////////////////////////////

  // Update the Rest of the Model Values when the user updates inputs
  @action updateModel(inputSignalChanged) {
    // Find Recommended Parts based on inputs
    this.model.findRecommendedParts();

    // Validate Project Inputs and Generate Graph
    this._validateAndUpdateProjectModel();

    if (this.model.validationResults.isAllValid) {
      // Build the internal circuit
      if (this.hasOperationalError || !this.model.validationResults.isOperatingRange || inputSignalChanged) {
        let minVarType = Math.min(this.model.projectData.varTypeLeft, this.model.projectData.varTypeRight),
          maxVarType = Math.max(this.model.projectData.varTypeLeft, this.model.projectData.varTypeRight),
          minVDiff = Math.min(this.model.projectData.vDiffLeft, this.model.projectData.vDiffRight),
          maxVDiff = Math.max(this.model.projectData.vDiffLeft, this.model.projectData.vDiffRight);

        for (let i = 1; i <= 4; i++) {
          switch (i) {
            case 1:
              this.set('model.projectData.vDiffSlider', maxVDiff);
              this.set('model.projectData.varTypeSlider', minVarType);
              break;
            case 2:
              this.set('model.projectData.vDiffSlider', minVDiff);
              this.set('model.projectData.varTypeSlider', maxVarType);
              break;
            case 3:
              this.set('model.projectData.vDiffSlider', maxVDiff);
              this.set('model.projectData.varTypeSlider', maxVarType);
              break;
            case 4:
              this.set('model.projectData.vDiffSlider', minVDiff);
              this.set('model.projectData.varTypeSlider', minVarType);
              break;
          }

          // Validate and Build using different combinations of slider values
          var valid = this._validateAndUpdateCircuitModel();
          if (!valid) {
            break;
          }
        }
      } else {
        // Just Validate and Build using the set slider values
        this._validateAndUpdateCircuitModel();
      }
    }
    // Set the Querystring values to the URL
    this._setUrl();

    // Save the previous operational state
    this.set('hasOperationalError', !this.model.validationResults.isOperatingRange);
  }

  @action updateCircuitModel() {
    // Build the internal circuit
    this._validateAndUpdateCircuitModel();
    this._setUrl();
  }

  @action updateSelectedTab(tab) {
    this.set('model.selectedTab', tab);
    let eventName = '';
    switch (tab) {
      case 1:
        eventName = 'plotTabViewed';
        break;
      case 2:
        eventName = 'circuitTabViewed';
        break;
      case 3:
        eventName = 'recommendedTabViewed';
        break;
    }
    this.analytics.setWebEvent(this.model.toolConfigData.toolName, eventName, '');
    this._setUrl();
  }

  @action giveFeedback(feedbackModel) {
    return Promise.resolve()
      .then(() => {
        this._systemDialogFunc('ON', 'applicationBusy');
        return delay(250).then(() => {
          return;
        });
      })
      .then(() => {
        return this.feedback.deliverFeedback(feedbackModel.name, feedbackModel.email, feedbackModel.feedback);
      })
      .then((result) => {
        this._systemDialogFunc('OFF', '');
        if (result.status === 0) {
          this.openDialog('modals.feedback.header', 'modals.feedback.feedbackDialogOkMessage');
        } else {
          this.openDialog('modals.feedback.header', 'modals.feedback.feedbackDialogNgMessage');
        }
      })
      .catch((error) => {
        this._systemDialogFunc('OFF', '');
        this.openDialog('modals.feedback.header', 'modals.feedback.feedbackDialogNgMessage');
        this.logger.log('Error:' + error.toString());
      });
  }

  @action insertingToolbar(element) {
    this.navElement = element;
    this._getDimensionsOnResize();
    this.boundResizeListener = this._getDimensionsOnResize.bind(this);
    window.addEventListener('resize', this.boundResizeListener);
  }

  @action destroyingToolbar() {
    this.navElement = null;
    window.removeEventListener('resize', this.boundResizeListener);
  }

  @action openDialog(title, message, msgparams) {
    let adjparams = msgparams;
    if (adjparams) {
      adjparams.htmlSafe = 'true';
    } else {
      adjparams = { htmlSafe: 'true' };
    }
    this.set('generalDialog.title', this.intl.t(title, { htmlSafe: 'true' }));
    this.set('generalDialog.message', this.intl.t(message, adjparams));
    this.set('generalDialog.enabled', true);
  }

  @action closeDialog() {
    this.set('generalDialog.enabled', false);
    this.set('generalDialog.title', '');
    this.set('generalDialog.message', '');
  }
}
